import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {store} from "../../store.ts";
import {authenticatedFetch} from "../../../services/authRuntime.ts";

export const MATRIX_CALL_METADATA_EVENT_TYPE = 'org.phonehq.metadata' as const;

interface MatrixCallMetadataContent {
    metadata: string;
    callId: string;
}

interface MatrixMetadataTimelineEvent {
    getType(): string;
    getContent(): unknown;
    getTs?(): number;
    getSender?(): string | undefined;
}

const MATRIX_CALL_METADATA_MAX_AGE_MS = 30_000;

interface AuthContext {
    tenant: string;
    userId: number;
}

interface CallMetadataState {
    metadata: string | null;
    isLoading: boolean;
    error: string | null;
    currentCallId: string | null;
    currentCallData: {
        callId: string | null;
        caller: number | null;
        callees: number[];
        roomId: string | null;
        startedAt: number | null;
        endedAt: number | null;
        status: 'ONGOING' | 'ENDED' | null;
    };
}

const initialState: CallMetadataState = {
    metadata: null,
    isLoading: false,
    error: null,
    currentCallId: null,
    currentCallData: {
        callId: null,
        caller: null,
        callees: [],
        roomId: null,
        startedAt: null,
        endedAt: null,
        status: null
    }
};

export enum CallType {
    INTERNAL = 'INTERNAL',
    CONFERENCE = 'CONFERENCE'
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function readCallId(value: unknown): string {
    if (typeof value === 'string' && value.length > 0) {
        return value;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value);
    }

    throw new Error('Call metadata does not contain a valid callId');
}

function getAuthContext(): AuthContext {
    const auth = store.getState().auth;
    const tenant = auth?.tenant;
    const userId = Number(auth?.userId);

    if (!tenant) {
        throw new Error('Tenant is not available in auth state');
    }

    if (!Number.isFinite(userId) || userId <= 0) {
        throw new Error('User ID is not available in auth state');
    }

    return {tenant, userId};
}

export function decodeCallMetadata(metadata: string): MatrixCallMetadataContent {
    const decodedMetadata: unknown = JSON.parse(atob(metadata));

    if (!isRecord(decodedMetadata) || !Array.isArray(decodedMetadata.metadata)) {
        throw new Error('Call metadata payload has an invalid shape');
    }

    const firstMetadataEntry = decodedMetadata.metadata[0];

    if (!isRecord(firstMetadataEntry)) {
        throw new Error('Call metadata payload does not contain metadata entries');
    }

    return {
        metadata,
        callId: readCallId(firstMetadataEntry.callId),
    };
}

export function readMatrixCallMetadataContent(content: unknown): MatrixCallMetadataContent | null {
    if (!isRecord(content) || typeof content.metadata !== 'string') {
        return null;
    }

    try {
        const metadataContent = decodeCallMetadata(content.metadata);
        return metadataContent.callId === readCallId(content.callId) ? metadataContent : null;
    } catch {
        return null;
    }
}

export function findMatrixCallMetadata(
    events: readonly MatrixMetadataTimelineEvent[] | undefined,
    callId?: string | null,
): MatrixCallMetadataContent | null {
    if (!events?.length) {
        return null;
    }

    const metadataEvents = events
        .filter(event => event.getType() === MATRIX_CALL_METADATA_EVENT_TYPE)
        .reverse();

    for (const event of metadataEvents) {
        const content = readMatrixCallMetadataContent(event.getContent());

        if (!content) {
            continue;
        }

        if (!callId || content.callId === callId) {
            return content;
        }
    }

    return null;
}

export function findMatrixCallMetadataForInvite(
    events: readonly MatrixMetadataTimelineEvent[] | undefined,
    matrixCallId: string,
): MatrixCallMetadataContent | null {
    if (!events?.length) {
        return null;
    }

    let inviteIndex = -1;
    for (let index = events.length - 1; index >= 0; index -= 1) {
        const event = events[index];
        if (event.getType() !== 'm.call.invite') {
            continue;
        }

        const content = event.getContent();
        if (isRecord(content) && content.call_id === matrixCallId) {
            inviteIndex = index;
            break;
        }
    }

    if (inviteIndex < 0) {
        return null;
    }

    const inviteTimestamp = events[inviteIndex].getTs?.();
    const inviteSender = events[inviteIndex].getSender?.();
    const candidates: MatrixCallMetadataContent[] = [];
    const explicit: MatrixCallMetadataContent[] = [];
    for (let index = inviteIndex - 1; index >= 0; index -= 1) {
        const event = events[index];
        if (event.getType().startsWith('m.call.')) break;
        if (event.getType() !== MATRIX_CALL_METADATA_EVENT_TYPE) continue;
        const metadataTimestamp = event.getTs?.();
        if (inviteTimestamp === undefined || metadataTimestamp === undefined ||
            inviteTimestamp - metadataTimestamp > MATRIX_CALL_METADATA_MAX_AGE_MS) break;
        if (metadataTimestamp > inviteTimestamp || (inviteSender && event.getSender?.() !== inviteSender)) continue;
        const content = event.getContent();
        const metadata = readMatrixCallMetadataContent(content);
        if (!metadata || !isRecord(content)) continue;
        if (content.matrixCallId === matrixCallId) explicit.push(metadata);
        if (!content.matrixCallId) candidates.push(metadata);
    }
    if (explicit.length) return explicit.length === 1 ? explicit[0] : null;
    return candidates.length === 1 ? candidates[0] : null;
}

export const fetchCallMetadata = createAsyncThunk(
    'callMetadata/fetch',
    async (params: { roomId: string; type: CallType }, { rejectWithValue }) => {
        try {
            const {tenant, userId} = getAuthContext();
            const body = {
                user: userId,
                tenant,
                'Event-Date-Timestamp': Date.now(),
                roomId: params.roomId,
                type: params.type
            };

            const response = await authenticatedFetch(`${window.__ENV__?.URL_CALLING_SERVICE}/telco/metadata/chat/outbound`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    tenant,
                },
                body: JSON.stringify(body)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const metadataString = await response.text();
            return decodeCallMetadata(metadataString);
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch metadata');
        }
    }
);

const callMetadataSlice = createSlice({
    name: 'callMetadata',
    initialState,
    reducers: {
        setCallMetadata: (state, action: PayloadAction<{ metadata: string; callId: string }>) => {
            state.metadata = action.payload.metadata;
            state.currentCallId = action.payload.callId;
            state.error = null;
        },
        clearCallMetadata: (state) => {
            state.metadata = null;
            state.currentCallId = null;
            state.error = null;
            state.isLoading = false;
        },
        setCallId: (state, action: PayloadAction<string>) => {
            state.currentCallId = action.payload;
        },
        setCurrentCallData: (state, action: PayloadAction<CallMetadataState['currentCallData']>) => {
            state.currentCallData = action.payload;
        },
        clearCurrentCallData: (state) => {
            state.currentCallData = initialState.currentCallData;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCallMetadata.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchCallMetadata.fulfilled, (state, action) => {
                state.isLoading = false;
                state.metadata = action.payload.metadata;
                state.currentCallId = action.payload.callId;
            })
            .addCase(fetchCallMetadata.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });
    },
});

export const { setCallMetadata, clearCallMetadata, setCurrentCallData } = callMetadataSlice.actions;
export const getCurrentCallData = (state: { callMetadataSlice: CallMetadataState }) => state.callMetadataSlice.currentCallData;
export const callMetadataReducer = callMetadataSlice.reducer;

export enum EventType {
    INVITE = 'INVITE',
    ANSWER = 'ANSWER',
    REJECT = 'REJECT',
    HANGUP = 'HANGUP',
    TECHNICAL_CLOSURE = 'TECHNICAL_CLOSURE'
}

interface BaseEvent {
    type: EventType;
    metadata: string;
    eventOwner: number;
    matrixCallId?: string;
    clientSessionId?: string;
    occurredAt: number;
    roomId: string;
}

interface InviteEvent extends BaseEvent {
    type: EventType.INVITE;
    caller: number;
    callees: number[];
    media?: 'audio' | 'video';
}

interface AnswerEvent extends BaseEvent {
    type: EventType.ANSWER;
}

interface RejectEvent extends BaseEvent {
    type: EventType.REJECT;
}

interface HangupEvent extends BaseEvent {
    type: EventType.HANGUP;
}

interface TechnicalClosureEvent extends BaseEvent {
    type: EventType.TECHNICAL_CLOSURE;
}

type MatrixEvent = InviteEvent | AnswerEvent | RejectEvent | HangupEvent | TechnicalClosureEvent;

function getCurrentMetadata(): string {
    const metadata = store.getState().callMetadataSlice.metadata;
    if (!metadata) {
        throw new Error('No call metadata available. Please start a call first.');
    }
    return metadata;
}

/**
 * Send a PUT request to the Matrix Events API using stored metadata
 */
async function sendMatrixEvent(
    eventData: MatrixEvent,
): Promise<Response> {
    const {tenant} = getAuthContext();

    const defaultHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
        tenant,
    };

    const requestOptions: RequestInit = {
        method: 'PUT',
        headers: defaultHeaders,
        body: JSON.stringify(eventData),
    };

    try {
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 15_000);
        let response: Response;
        try {
            response = await authenticatedFetch(`${window.__ENV__?.AXON_BASE_URL}/api/v1/matrix-events`,
                {...requestOptions, signal: controller.signal});
        } finally {
            window.clearTimeout(timeout);
        }

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}, statusText: ${response.statusText}`);
        }

        return response;
    } catch (error) {
        console.error('Error sending matrix event:', error);
        throw error;
    }
}

/**
 * Send INVITE event using stored metadata
 */
async function sendInviteEvent(): Promise<Response> {

    const callData = store.getState().callMetadataSlice.currentCallData;
    const metadata = getCurrentMetadata();
    const {userId} = getAuthContext();

    if (!callData.roomId) {
        throw new Error('No room ID available in call data');
    }

    if (callData.caller !== userId) {
        throw new Error('Call caller must match the authenticated user');
    }

    if (!callData.callees.length) {
        throw new Error('Invite event requires at least one callee');
    }

    if (callData.callees.some(callee => !Number.isFinite(callee) || callee <= 0)) {
        throw new Error('Invite event contains an invalid callee');
    }

    const eventData: InviteEvent = {
        type: EventType.INVITE,
        metadata,
        eventOwner: userId,
        occurredAt: Date.now(),
        roomId: callData.roomId,
        caller: userId,
        callees: callData.callees,
    };

    return sendMatrixEvent(eventData);
}

/**
 * Send ANSWER event using stored metadata
 */
async function sendAnswerEvent(
): Promise<Response> {
    const metadata = getCurrentMetadata();
    const {userId} = getAuthContext();
    const callData = store.getState().callMetadataSlice.currentCallData;

    if (!callData.roomId) {
        throw new Error('No room ID available in call data');
    }

    const eventData: AnswerEvent = {
        type: EventType.ANSWER,
        metadata,
        eventOwner: userId,
        occurredAt: Date.now(),
        roomId: callData.roomId,
    };

    return sendMatrixEvent(eventData);
}

/**
 * Send REJECT event using stored metadata
 */
async function sendRejectEvent(): Promise<Response> {
    const metadata = getCurrentMetadata();
    const {userId} = getAuthContext();
    const callData = store.getState().callMetadataSlice.currentCallData;

    if (!callData.roomId) {
        throw new Error('No room ID available in call data');
    }

    const eventData: RejectEvent = {
        roomId: callData.roomId,
        type: EventType.REJECT,
        metadata,
        eventOwner: userId,
        occurredAt: Date.now(),
    };

    return sendMatrixEvent(eventData);
}

/**
 * Send HANGUP event using stored metadata
 */
async function sendHangupEvent(): Promise<Response> {
    const metadata = getCurrentMetadata();
    const {userId} = getAuthContext();
    const callData = store.getState().callMetadataSlice.currentCallData;

    if (!callData.roomId) {
        throw new Error('No room ID available in call data');
    }

    const eventData: HangupEvent = {
        type: EventType.HANGUP,
        metadata,
        eventOwner: userId,
        occurredAt: Date.now(),
        roomId: callData.roomId
    };

    return sendMatrixEvent(eventData);
}

/**
 * Send TECHNICAL_CLOSURE event using stored metadata
 */
async function sendTechnicalClosureEvent(
    roomId: string,
    metadata: string,
): Promise<Response> {
    const {userId} = getAuthContext();

    const eventData: TechnicalClosureEvent = {
        type: EventType.TECHNICAL_CLOSURE,
        metadata,
        eventOwner: userId,
        occurredAt: Date.now(),
        roomId
    };

    return sendMatrixEvent(eventData);
}

export {
    sendMatrixEvent,
    sendInviteEvent,
    sendAnswerEvent,
    sendRejectEvent,
    sendHangupEvent,
    sendTechnicalClosureEvent,
};
